import React, { useState } from "react";
import "../Style/Whychoose.css";

import craftedIcon from "../assets/crafted.png";
import structuredIcon from "../assets/structured.png";
import codingIcon from "../assets/coding.png";
import protectionIcon from "../assets/protection.png";
import teamIcon from "../assets/team.png";
import resultIcon from "../assets/result.png";
import supportIcon from "../assets/support.png";
import maintenanceIcon from "../assets/maintenance.png";
import deliveryIcon from "../assets/delivery.png";
import satisfactionIcon from "../assets/satisfaction.png";
import whyIllustration from "../assets/Why_03.png";
import commitmentIcon from "../assets/commitment.png";
import excellenceIcon from "../assets/excellence.png";
import passionIcon from "../assets/passion.png";
import teamworkIcon from "../assets/teamwork.png";
import priorityIcon from "../assets/priority.png";
import arrowIcon from "../assets/arrow.png";

const SLIDE_COUNT = 5;
const AUTOPLAY_INTERVAL = 3200; // ms — not too fast, not too slow

// Reveals a section (and its staggered children, via CSS) the first time
// it scrolls into view — used by the mobile stacked layout so every
// section animates in one after another, the same way, as you scroll.
const useInView = (threshold = 0.2) => {
  const ref = React.useRef(null);
  const [inView, setInView] = useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(node);
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
};

const features = [
  { icon: craftedIcon, title: "Crafted Design", desc: "Visually engrossing designs that captivate audiences" },
  { icon: structuredIcon, title: "Structured Web Framework", desc: "Scalable architecture for seamless growth" },
  { icon: codingIcon, title: "Secure Coding Techniques", desc: "Robust defense against cyber threats" },
  { icon: protectionIcon, title: "Advanced Web Protection", desc: "Cutting-edge security measures for safety" },
  { icon: teamIcon, title: "Dedicated Team", desc: "Experts committed to client success" },
  { icon: resultIcon, title: "Result-Oriented Approach", desc: "Strategies for achieving desired outcomes" },
  { icon: supportIcon, title: "24/7 Email Support", desc: "Round-the-clock assistance for client convenience" },
  { icon: maintenanceIcon, title: "Maintenance Support", desc: "Reliable upkeep for website performance" },
  { icon: deliveryIcon, title: "On-time Delivery", desc: "Timely completion of web projects" },
  { icon: satisfactionIcon, title: "100% Client Satisfaction", desc: "Client fulfillment by exceeding expectations" },
];

const stats = [
  { percent: "88%", desc: "Of consumers trust a business more after visiting a professional website" },
  { percent: "91%", desc: "Check a company's website before making a purchase decision" },
  { percent: "67%", desc: "Are more likely to buy from mobile-optimized websites" },
  { percent: "53%", desc: "Leave a site if it takes longer than 3 seconds to load" },
  { percent: "78%", desc: "Judge business credibility based on website design" },
  { percent: "84%", desc: "Expect businesses to have an online presence in 2025" },
];

const values = [
  { icon: commitmentIcon, title: "Commitment", desc: "We don't stop until we reach our goals." },
  { icon: excellenceIcon, title: "Excellence", desc: "We don't settle for less, as we have set our standards high." },
  { icon: passionIcon, title: "Passion", desc: "We go beyond our comfort-zone to develop tools that promise enhanced customer empowerment." },
  { icon: teamworkIcon, title: "Teamwork", desc: "We operate in an inclusive work environment with a diverse pool of talent." },
  { icon: priorityIcon, title: "Priority", desc: "Customers are our top priority. Customer satisfaction is our goal." },
];

const WhyChoose = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentSlide, setCurrentSlide] = useState(0);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-advancing carousel — loops continuously, always running
  React.useEffect(() => {
    if (isMobile) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDE_COUNT);
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [isMobile]);

  // One reveal hook per mobile section — each fires independently the
  // moment that section scrolls into view, so they animate in order
  // as the user scrolls, not all at once on page load.
  const [heroRef, heroInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Mobile view - plain vertical stack, same reveal animation on every section
  if (isMobile) {
    return (
      <div className="why-choose-mobile">
        <section className="why-slide-mobile why-section-mobile">
          <div
            ref={heroRef}
            className={`why-content-mobile reveal-mobile ${heroInView ? "in-view" : ""}`}
          >
            <h1 className="why-small-mobile">Why</h1>
            <h1 className="why-big-mobile">choose MPeoples?</h1>
          </div>
        </section>

        <section className="why-slide-mobile features-section-mobile">
          <div
            ref={featuresRef}
            className={`features-grid-mobile reveal-mobile ${featuresInView ? "in-view" : ""}`}
          >
            {features.slice(0, 6).map((f, i) => (
              <div className="feature-card-mobile" key={i}>
                <div className="feature-icon-box-mobile">
                  <img src={f.icon} alt={f.title} className="feature-icon-mobile" />
                </div>
                <h3 className="feature-title-mobile">{f.title}</h3>
                <p className="feature-desc-mobile">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="why-slide-mobile stats-section-mobile">
          <div
            ref={statsRef}
            className={`stats-inner-mobile reveal-mobile ${statsInView ? "in-view" : ""}`}
          >
            <h2 className="stats-heading-mobile">
              Why Does Your Business Need a Website in 2026?
            </h2>
            <div className="stats-grid-mobile">
              {stats.map((s, i) => (
                <div className="stat-card-mobile" key={i}>
                  <span className="stat-percent-mobile">{s.percent}</span>
                  <p className="stat-desc-mobile">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="why-slide-mobile values-section-mobile">
          <div
            ref={valuesRef}
            className={`values-inner-mobile reveal-mobile ${valuesInView ? "in-view" : ""}`}
          >
            <p className="values-label-mobile">WHAT WE BELIEVE IN?</p>
            <h2 className="values-heading-mobile">
              Our values that inspire us to push forward everyday.
            </h2>
            <div className="values-grid-mobile">
              {values.map((v, i) => (
                <div className="value-card-mobile" key={i}>
                  <div className="value-icon-box-mobile">
                    <img src={v.icon} alt={v.title} className="value-icon-mobile" />
                  </div>
                  <h3 className="value-title-mobile">{v.title}</h3>
                  <p className="value-desc-mobile">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="why-slide-mobile cta-section-mobile">
          <div
            ref={ctaRef}
            className={`cta-inner-mobile reveal-mobile ${ctaInView ? "in-view" : ""}`}
          >
            <p className="cta-label-mobile">Have a project in mind?</p>
            <h2 className="cta-heading-mobile">
              Let's make something
              <br />
              great together!
            </h2>
            <button className="cta-btn-mobile">
              <img src={arrowIcon} alt="arrow" className="cta-arrow-mobile" />
            </button>
          </div>
        </section>
      </div>
    );
  }

  // Desktop view - all slides in a row, auto-advances on its own (no arrows)
  return (
    <div className="why-outer">
      <div className="why-sticky">
        <div
          className="why-track"
          style={{
            transform: `translate3d(-${currentSlide * (100 / SLIDE_COUNT)}%, 0, 0)`,
          }}
        >
          <section className="why-slide why-section">
            <div className="why-content">
              <h1 className="why-small">Why</h1>
              <h1 className="why-big">Choose MPeoples?</h1>
            </div>
          </section>

          <section className="why-slide features-section">
            <div className="features-grid">
              {features.map((f, i) => (
                <div className="feature-card" key={i}>
                  <div className="feature-icon-box">
                    <img src={f.icon} alt={f.title} className="feature-icon" />
                  </div>
                  <h3 className="feature-title">{f.title}</h3>
                  <p className="feature-desc">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="why-slide stats-section">
            <div className="stats-inner">
              <h2 className="stats-heading">
                Why Does Your Business Need a Website in 2026?
              </h2>
              <div className="stats-body">
                <div className="stats-grid">
                  {stats.map((s, i) => (
                    <div className="stat-card" key={i}>
                      <span className="stat-percent">{s.percent}</span>
                      <p className="stat-desc">{s.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="stats-illustration">
                  <img src={whyIllustration} alt="illustration" />
                </div>
              </div>
            </div>
          </section>

          <section className="why-slide values-section">
            <div className="values-inner">
              <p className="values-label">WHAT WE BELIEVE IN?</p>
              <h2 className="values-heading">
                Our values that inspire us to push forward everyday.
              </h2>
              <div className="values-row">
                {values.map((v, i) => (
                  <div className="value-card" key={i}>
                    <img src={v.icon} alt={v.title} className="value-icon" />
                    <h3 className="value-title">{v.title}</h3>
                    <p className="value-desc">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="why-slide cta-section">
            <div className="cta-inner">
              <p className="cta-label">Have a project in mind?</p>
              <h2 className="cta-heading">
                Let's make something
                <br />
                great together!
              </h2>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WhyChoose;